// @flow
/* eslint-disable no-underscore-dangle */
import * as React from 'react'
import shallowEqual from 'mz-utils/shallowEqual'
import FieldController from './FieldController'
import { isSyntheticEvent, isNil } from '../../utils'
import { pickExcept } from './utils'
import messages from '../../messages.intl'

// Types
import type { Input, Meta } from '../../core/BaseField'
import type { NestOptions, ControllerUpdate } from './FieldController'

export type NestFunc = (name: string, options?: NestOptions) => {}

export type RenderProps = {
  input: Input,
  meta: Meta,
  required: boolean,
}

export type RenderFunc = (
  props: RenderProps,
  nest: NestFunc,
  options: Object,
  controller: FieldController
) => React.Node

type PropsShape = {
  __optimizeUpdates: boolean,
  controller: FieldController,
  initValue: any,
  emptyValue: any,
  input: Input,
  meta: Meta,
  render: RenderFunc,
  onMetaChange: (meta: Meta, controller: FieldController) => any,
  required: boolean,
  validate: (
    newValue: any,
    options: Object,
    controller: FieldController
  ) => any,
  normalize: (newValue: any, currValue: any, options: Object) => any,
  options: Object,
}

export type Props = $Shape<PropsShape>

export type AllPropsEnum = $Exact<$ObjMap<PropsShape, () => boolean>>

// Utils
const STOP_PROPAGATION = {}

const FIELD_PROPS: AllPropsEnum = {
  __optimizeUpdates: true,
  controller: true,
  initValue: true,
  emptyValue: true,
  input: true,
  meta: true,
  render: true,
  onMetaChange: true,
  required: true,
  validate: true,
  normalize: true,
  options: true,
}

/**
 * Wrapper around static BaseField which adds logic around `meta`
 * prop to be able to use the field without any external controller,
 * like `Field` in `redux-form`
 */
class Field extends React.Component<Props> {
  static defaultProps = {
    input: {},
    meta: {},
  }

  mounted = false

  unmounted = false

  internalController = null

  constructor(props: Props) {
    super(props)

    if (!props.controller) {
      const { emptyValue } = props
      const initMeta = props.meta
      const initValue =
        'value' in props.input ? props.input.value : props.initValue

      this.internalController = new FieldController({
        initValue,
        initMeta,
        emptyValue,
      })
    }

    this.controller.subscribe(this.handleControllerUpdates, {
      validate: this.validateValue,
    })
  }

  get controller(): FieldController {
    if (this.internalController) {
      return this.internalController
    }
    if (this.props.controller) {
      return this.props.controller
    }
    throw new Error('Controller is not defined')
  }

  get options() {
    return {
      ...pickExcept(this.props, FIELD_PROPS),
      ...this.props.options,
    }
  }

  componentDidMount() {
    this.mounted = true
    this.handleMetaChange()
  }

  componentWillUnmount() {
    this.unmounted = true
    this.controller.unsubscribe(this.handleControllerUpdates)
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (
      !shallowEqual(nextProps.input.value, this.props.input.value) &&
      !shallowEqual(nextProps.input.value, this.controller.value)
    ) {
      this.controller.setValue(nextProps.input.value, { updatedOutside: true })
    }

    if (
      !shallowEqual(nextProps.meta, this.props.meta) &&
      !shallowEqual(nextProps.meta, this.controller.meta)
    ) {
      this.controller.setMeta(nextProps.meta, { updatedOutside: true })
    }
  }

  shouldComponentUpdate(nextProps: Props): boolean {
    return (
      !nextProps.__optimizeUpdates ||
      !shallowEqual(nextProps.options, this.props.options) ||
      !shallowEqual(nextProps, this.props, { exclude: FIELD_PROPS }) ||
      nextProps.controller !== this.props.controller
    )
  }

  validateValue = (val: any) => {
    if (this.props.required && isNil(val)) {
      return messages.REQUIRED
    }
    if (this.props.validate) {
      return this.props.validate(val, this.options, this.controller)
    }
    return null
  }

  nestChildField = (name: string, options?: NestOptions) => {
    const controller = this.controller.getOrCreateChild(name, options)
    return { name, controller }
  }

  handleMetaChange = () => {
    const { onMetaChange } = this.props
    onMetaChange && onMetaChange(this.controller.meta, this.controller)
  }

  handleValueChange = () => {
    const {
      input: { onChange },
    } = this.props
    onChange && onChange(this.controller.value, STOP_PROPAGATION)
  }

  handleControllerUpdates = (updated: ControllerUpdate) => {
    if (updated.value && !updated.updatedOutside) {
      this.handleValueChange()
    }

    if (updated.meta && !updated.updatedOutside) {
      this.handleMetaChange()
    }

    if (updated.view && !this.unmounted && this.mounted) {
      this.forceUpdate()
    }
  }

  handleFocus = (e: any, opts?: Object) => {
    if (opts === STOP_PROPAGATION) return
    this.handleChangeMeta({ active: true })

    const {
      input: { onFocus },
    } = this.props
    onFocus && onFocus(e, STOP_PROPAGATION)
  }

  handleBlur = (e: any, opts?: Object) => {
    if (opts === STOP_PROPAGATION) return
    this.handleChangeMeta({ active: false, touched: true })

    const {
      input: { onBlur },
    } = this.props
    onBlur && onBlur(e, STOP_PROPAGATION)
  }

  handleChangeMeta = (metaUpdate: ?Meta) => {
    this.controller.setMeta({
      ...this.controller.meta,
      ...metaUpdate,
    })
  }

  handleChangeValue = (e: any, opts?: Object) => {
    if (opts === STOP_PROPAGATION) return

    const value = isSyntheticEvent(e) ? e.target.value : e
    const normValue = this.props.normalize
      ? this.props.normalize(value, this.controller.value, this.options)
      : value

    this.controller.setValue(normValue)
  }

  setFieldDOMNode = (node: any) => {
    this.controller.setFieldDOMNode(node)
  }

  getHandledInput(): Input {
    return {
      ...this.props.input,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      onChange: this.handleChangeValue,
      value: this.controller.value,
    }
  }

  render() {
    const { controller, options } = this
    const { meta } = controller
    const input = this.getHandledInput()
    const props = {
      input,
      meta,
      required: !!this.props.required,
      containerRef: this.setFieldDOMNode,
      controller,
    }

    return this.props.render
      ? this.props.render(props, this.nestChildField, options, controller)
      : null
  }
}

export default Field
